import { createAsyncThunk, createSelector } from "@reduxjs/toolkit"

import { selectIsSplitView } from "./app-slice"
import {
  closeDetailSheet,
  selectDetailSheetExpanded,
  selectDetailSheetOpen,
} from "./detail-sheet-slice"
import {
  closeFlightSheet,
  selectFlightSheetExpanded,
  selectFlightSheetOpen,
} from "./flight-search-slice"
import type { AppDispatch, RootState } from "./redux-store"
import { voiceChatActions } from "./voice-chat-slice"

// Sheet coverage of the voice overlay.
//
// A sheet opened from inside voice mode (tapping a place card in the voice
// carousel) paints on top of the overlay — sheets sit at `z-overlay` (20) and
// render after `<main>`, whose `isolate` caps the overlay's z-3. Outside split
// view that sheet is a near-full-screen drawer, so it hides voice and takes the
// controls with it. Voice hands the surface over rather than fighting for it:
// while covered, the mini-player rides above the sheet so mute and end stay
// reachable, and the session runs on underneath.
//
// Whether a sheet covers voice is a layout question. Outside split view
// `Sheet` presents a right-side sheet as a full-height bottom drawer — a
// takeover, so any open sheet covers voice. In split view the sheet
// takes the right half while the overlay takes the left, so they coexist; only
// expanding the sheet to full width covers voice.
//
const sheetCoversVoiceOverlay = (
  isSplitView: boolean,
  open: boolean,
  expanded: boolean
) => open && (!isSplitView || expanded)

export const selectVoiceOverlayCoveredBySheet = createSelector(
  [
    selectIsSplitView,
    selectDetailSheetOpen,
    selectDetailSheetExpanded,
    selectFlightSheetOpen,
    selectFlightSheetExpanded,
  ],
  (isSplitView, detailOpen, detailExpanded, flightOpen, flightExpanded) =>
    sheetCoversVoiceOverlay(isSplitView, detailOpen, detailExpanded) ||
    sheetCoversVoiceOverlay(isSplitView, flightOpen, flightExpanded)
)

// Put the full overlay back, whatever is standing in for it. Backs the
// mini-player's expand control and the composer's voice button during a live
// call: on the origin chat there is nowhere to navigate back to, so restoring
// means clearing the two things that can hide the overlay — the user's own
// collapse, and any sheet lying on top of it. Both are cleared unconditionally
// because either may be in play, and neither reducer cares if it wasn't.
export const restoreVoiceOverlay = createAsyncThunk<
  void,
  void,
  { state: RootState; dispatch: AppDispatch }
>("voiceChat/restoreOverlay", async (_, { dispatch, getState }) => {
  dispatch(voiceChatActions.voiceModeExpanded())
  const state = getState()
  if (selectDetailSheetOpen(state)) {
    await dispatch(closeDetailSheet())
  }
  if (selectFlightSheetOpen(state)) {
    await dispatch(closeFlightSheet())
  }
})
